import React, { useEffect, useState } from 'react';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Cell } from 'recharts';
import { Printer, TrendingUp, TrendingDown, Landmark, PiggyBank } from 'lucide-react';
import api from '../lib/api';
import { PageHeader, Button, Card, StatCard, Th, Td } from '../components/ui';
import { fmtMoney } from '../lib/utils';

const COLORS = ['#1E3A8A', '#B45309', '#0891B2', '#65A30D', '#7C3AED', '#DB2777'];

export default function Reports() {
  const [r, setR] = useState(null);
  useEffect(() => { api.get('/reports/summary').then((res) => setR(res.data)); }, []);
  if (!r) return <div className="text-slate-400 animate-pulse">Loading reports…</div>;

  const catData = Object.entries(r.expenseByCategory).map(([category, amount]) => ({ category: category.replace('_', ' '), amount }));

  return (
    <div>
      <PageHeader title="Financial Reports" subtitle="Profit & loss overview"
        actions={<Button variant="outline" onClick={() => window.print()} data-testid="print-report-btn"><Printer size={16} /> Print</Button>} />

      <div id="print-area">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <StatCard label="Revenue" value={fmtMoney(r.revenue)} icon={TrendingUp} />
          <StatCard label="Collected" value={fmtMoney(r.collected)} icon={PiggyBank} accent="text-emerald-600" />
          <StatCard label="Total Expenses" value={fmtMoney(r.totalExpenses)} icon={TrendingDown} accent="text-rose-600" />
          <StatCard label="Net Profit" value={fmtMoney(r.netProfit)} icon={Landmark} accent={r.netProfit >= 0 ? 'text-emerald-600' : 'text-rose-600'} />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
          <Card className="p-4">
            <h3 className="text-sm font-heading font-bold text-slate-800 mb-4">Revenue Breakdown</h3>
            <table className="w-full">
              <tbody className="divide-y divide-slate-100">
                <Line label="Wholesale Revenue" value={r.wholesaleRevenue} />
                <Line label="Retail Revenue" value={r.retailRevenue} />
                <Line label="Total Revenue" value={r.revenue} bold />
                <Line label="Amount Collected" value={r.collected} />
                <Line label="Receivables (Outstanding)" value={r.receivables} accent />
                <Line label="Total Purchases" value={r.totalPurchases} />
              </tbody>
            </table>
          </Card>

          <Card className="p-4">
            <h3 className="text-sm font-heading font-bold text-slate-800 mb-4">Expenses by Category</h3>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={catData} margin={{ left: -12, right: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" vertical={false} />
                <XAxis dataKey="category" tick={{ fontSize: 10, fill: '#94A3B8' }} />
                <YAxis tick={{ fontSize: 11, fill: '#94A3B8' }} />
                <Tooltip formatter={(v) => fmtMoney(v)} contentStyle={{ fontSize: 12, borderRadius: 6 }} />
                <Bar dataKey="amount" radius={[4, 4, 0, 0]} barSize={30}>
                  {catData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Card>
        </div>

        <Card className="p-5">
          <h3 className="text-sm font-heading font-bold text-slate-800 mb-4">Profit & Loss Statement</h3>
          <div className="max-w-lg space-y-2">
            <PL label="Total Revenue" value={r.revenue} />
            <PL label="(−) Total Expenses" value={-r.totalExpenses} />
            <div className="border-t-2 border-slate-800 pt-2 mt-2">
              <PL label="Net Profit / (Loss)" value={r.netProfit} bold />
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}

function Line({ label, value, bold, accent }) {
  return (
    <tr>
      <Td className={bold ? 'font-semibold text-slate-900' : 'text-slate-600'}>{label}</Td>
      <Td right className={`${bold ? 'font-bold' : ''} ${accent ? 'text-rose-600' : ''}`}>{fmtMoney(value)}</Td>
    </tr>
  );
}

function PL({ label, value, bold }) {
  return (
    <div className="flex justify-between items-center">
      <span className={`text-sm ${bold ? 'font-heading font-bold text-slate-900' : 'text-slate-600'}`}>{label}</span>
      <span className={`num ${bold ? 'font-heading font-extrabold text-lg' : ''} ${value < 0 ? 'text-rose-600' : 'text-slate-900'}`}>{fmtMoney(value)}</span>
    </div>
  );
}
